(() => {
  const SELECTOR = '[data-ap-us-impact-preorder]';
  const KEY = '__apUSImpactPreorder';

  const parseJSON = (text) => {
    try {
      return JSON.parse(text);
    } catch (error) {
      console.error(
        '[ALLPOWERS Pre-order] Invalid variant JSON:',
        error
      );

      return [];
    }
  };

  const parseDate = (value) => {
    if (!value) return null;

    const [year, month, day] = String(value)
      .split('-')
      .map(Number);

    if (!year || !month || !day) {
      return null;
    }

    return new Date(
      Date.UTC(year, month - 1, day)
    );
  };

  const usDate = (date) => {
    if (!date) return '';

    return new Intl.DateTimeFormat(
      'en-US',
      {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
      }
    ).format(date);
  };

  const usShortDate = (date) => {
    if (!date) return '';

    return new Intl.DateTimeFormat(
      'en-US',
      {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
      }
    ).format(date);
  };

  const formatShippingRange = (
    startValue,
    endValue,
    fallback = ''
  ) => {
    const start = parseDate(startValue);
    const end = parseDate(endValue);

    if (start && end) {
      const sameYear =
        start.getUTCFullYear() ===
        end.getUTCFullYear();

      const sameMonth =
        sameYear &&
        start.getUTCMonth() ===
        end.getUTCMonth();

      if (sameMonth) {
        const month =
          new Intl.DateTimeFormat(
            'en-US',
            {
              month: 'long',
              timeZone: 'UTC'
            }
          ).format(start);

        return (
          `${month} ` +
          `${start.getUTCDate()}–` +
          `${end.getUTCDate()}, ` +
          `${end.getUTCFullYear()}`
        );
      }

      return (
        `${usDate(start)}–` +
        `${usDate(end)}`
      );
    }

    if (end) {
      return `By ${usDate(end)}`;
    }

    if (start) {
      return `From ${usDate(start)}`;
    }

    return (
      fallback ||
      'Ship date will be confirmed soon.'
    );
  };

  const formatBadgeRange = (
    startValue,
    endValue
  ) => {
    const start = parseDate(startValue);
    const end = parseDate(endValue);

    if (start && end) {
      const sameYear =
        start.getUTCFullYear() ===
        end.getUTCFullYear();

      const sameMonth =
        sameYear &&
        start.getUTCMonth() ===
        end.getUTCMonth();

      if (sameMonth) {
        const month =
          new Intl.DateTimeFormat(
            'en-US',
            {
              month: 'short',
              timeZone: 'UTC'
            }
          ).format(start);

        return (
          `${month} ` +
          `${start.getUTCDate()}–` +
          `${end.getUTCDate()}`
        );
      }

      return (
        `${usShortDate(start)}–` +
        `${usShortDate(end)}`
      );
    }

    if (end) {
      return `By ${usShortDate(end)}`;
    }

    if (start) {
      return `From ${usShortDate(start)}`;
    }

    return '';
  };

  const earliestDateValue = (values) => {
    const valid = values
      .map((value) => ({
        value,
        date: parseDate(value)
      }))
      .filter((item) => item.date);

    if (!valid.length) return null;

    valid.sort(
      (a, b) =>
        a.date.getTime() -
        b.date.getTime()
    );

    return valid[0].value;
  };

  const latestDateValue = (values) => {
    const valid = values
      .map((value) => ({
        value,
        date: parseDate(value)
      }))
      .filter((item) => item.date);

    if (!valid.length) return null;

    valid.sort(
      (a, b) =>
        b.date.getTime() -
        a.date.getTime()
    );

    return valid[0].value;
  };

  const cleanLabel = (value) => {
    return String(value || '').trim();
  };

  /**
   * 支持：
   * P300
   *
   * 或：
   * P300|SE100 Solar Panel
   */
  const splitItemLabels = (value) => {
    const labels = cleanLabel(value)
      .split('|')
      .map((label) => label.trim())
      .filter(Boolean);

    return {
      first: labels[0] || '',
      second: labels[1] || ''
    };
  };

  const buildShipDateLabel = (
    itemName
  ) => {
    const name = cleanLabel(itemName);

    if (!name) {
      return 'Estimated Ship Date';
    }

    return `${name} Estimated Ship Date`;
  };

  const isBundleContext = (
    productTitle,
    variantTitle
  ) => {
    const text = (
      `${cleanLabel(productTitle)} ` +
      `${cleanLabel(variantTitle)}`
    ).toLowerCase();

    return (
      text.includes('+') ||
      text.includes('bundle') ||
      text.includes('kit') ||
      text.includes('generator')
    );
  };

  const joinItemNames = (
    first,
    second
  ) => {
    const names = [
      cleanLabel(first),
      cleanLabel(second)
    ].filter(Boolean);

    if (!names.length) {
      return '';
    }

    if (names.length === 1) {
      return names[0];
    }

    return `${names[0]} and ${names[1]}`;
  };

  /**
   * 自动生成预售说明。
   *
   * 单个套装预售对象：
   * Only the SP-039 in this bundle is on pre-order...
   *
   * 两个套装预售对象：
   * Only the P300 and SE100 Solar Panel in this bundle are on pre-order...
   */
  const buildAutomaticShortNote = ({
    firstItemName,
    secondItemName,
    productTitle,
    variantTitle,
    fallback
  }) => {
    const itemName = joinItemNames(
      firstItemName,
      secondItemName
    );

    if (!itemName) {
      return (
        cleanLabel(fallback) ||
        'Reserve yours now. Your order will ship once stock arrives.'
      );
    }

    const hasTwoItems =
      Boolean(
        cleanLabel(firstItemName) &&
        cleanLabel(secondItemName)
      );

    if (
      isBundleContext(
        productTitle,
        variantTitle
      )
    ) {
      return hasTwoItems
        ? (
            `Only the ${itemName} in this bundle ` +
            `are on pre-order. In-stock items may ` +
            `ship separately and arrive earlier.`
          )
        : (
            `Only the ${itemName} in this bundle ` +
            `is on pre-order. In-stock items may ` +
            `ship separately and arrive earlier.`
          );
    }

    return hasTwoItems
      ? (
          `${itemName} are on pre-order. ` +
          `Your order will ship once stock arrives.`
        )
      : (
          `${itemName} is on pre-order. ` +
          `Your order will ship once stock arrives.`
        );
  };

  const getVariantShippingData = (
    variant,
    fallback
  ) => {
    const labels =
      splitItemLabels(
        variant.preorderItemLabel
      );

    const general = {
      start: variant.dispatchStart,
      end: variant.dispatchEnd,
      text: formatShippingRange(
        variant.dispatchStart,
        variant.dispatchEnd,
        fallback
      )
    };

    const firstItem = {
      start: variant.firstItemStart,
      end: variant.firstItemEnd,
      text: formatShippingRange(
        variant.firstItemStart,
        variant.firstItemEnd,
        fallback
      )
    };

    const secondItem = {
      start: variant.secondItemStart,
      end: variant.secondItemEnd,
      text: formatShippingRange(
        variant.secondItemStart,
        variant.secondItemEnd,
        fallback
      )
    };

    const hasFirstItemDate =
      Boolean(
        firstItem.start ||
        firstItem.end
      );

    const hasSecondItemDate =
      Boolean(
        secondItem.start ||
        secondItem.end
      );

    if (
      hasFirstItemDate &&
      !hasSecondItemDate
    ) {
      return {
        labels,
        showGeneral: false,
        showFirstItem: true,
        showSecondItem: false,
        general,
        firstItem,
        secondItem,
        firstItemLabel:
          labels.first ||
          'Pre-order Item',
        secondItemLabel: '',
        badgeStart:
          firstItem.start,
        badgeEnd:
          firstItem.end
      };
    }

    if (
      !hasFirstItemDate &&
      hasSecondItemDate
    ) {
      return {
        labels,
        showGeneral: false,
        showFirstItem: true,
        showSecondItem: false,
        general,
        firstItem: secondItem,
        secondItem,
        firstItemLabel:
          labels.first ||
          'Pre-order Item',
        secondItemLabel: '',
        badgeStart:
          secondItem.start,
        badgeEnd:
          secondItem.end
      };
    }

    if (
      hasFirstItemDate &&
      hasSecondItemDate
    ) {
      return {
        labels,
        showGeneral: false,
        showFirstItem: true,
        showSecondItem: true,
        general,
        firstItem,
        secondItem,
        firstItemLabel:
          labels.first ||
          'First Pre-order Item',
        secondItemLabel:
          labels.second ||
          'Second Pre-order Item',
        badgeStart:
          earliestDateValue([
            firstItem.start,
            firstItem.end,
            secondItem.start,
            secondItem.end
          ]),
        badgeEnd:
          latestDateValue([
            firstItem.start,
            firstItem.end,
            secondItem.start,
            secondItem.end
          ])
      };
    }

    return {
      labels,
      showGeneral: true,
      showFirstItem: false,
      showSecondItem: false,
      general,
      firstItem,
      secondItem,
      generalLabel:
        labels.first || '',
      firstItemLabel: '',
      secondItemLabel: '',
      badgeStart:
        general.start,
      badgeEnd:
        general.end
    };
  };

  class APUSImpactPreorder {
    constructor(root) {
      this.root = root;
      this.sectionId =
        root.dataset.sectionId;

      this.section =
        root.closest(
          '.shopify-section'
        ) ||
        document.getElementById(
          `shopify-section-${this.sectionId}`
        );

      this.variants =
        parseJSON(
          root.querySelector(
            '[data-ap-us-impact-preorder-variants]'
          )?.textContent || '[]'
        );

      this.map =
        new Map(
          this.variants.map(
            (variant) => [
              String(variant.id),
              variant
            ]
          )
        );

      this.cleanup = [];
      this.timer = null;

      if (!this.section) return;

      this.find();
      this.place();
      this.observe();
      this.renderSoon();
      this.scheduleExpiry();
    }

    find() {
      this.formId =
        `product-form-` +
        `${this.root.dataset.productId}-` +
        `${this.sectionId}`;

      this.form =
        document.getElementById(
          this.formId
        );

      this.idInput =
        this.form?.querySelector(
          'input[name="id"]'
        );

      this.gallery =
        this.section.querySelector(
          '.product-gallery__media-list-wrapper'
        );

      this.info =
        this.section.querySelector(
          'safe-sticky.product-info'
        );

      this.inventoryBlock =
        this.section.querySelector(
          '.product-info__inventory'
        );

      this.variantInventory =
        this.inventoryBlock?.querySelector(
          'variant-inventory'
        );

      this.buyBlock =
        this.section.querySelector(
          '.product-info__buy-buttons'
        );

      this.buyButtons =
        this.buyBlock?.querySelector(
          'buy-buttons'
        );

      this.atcButton =
        this.buyButtons?.querySelector(
          'button[type="submit"]'
        );

      this.atcText =
        this.atcButton?.querySelector(
          'span'
        ) ||
        this.atcButton;

      this.badge =
        this.root.querySelector(
          '[data-ap-us-po-badge]'
        );

      this.badgeItem =
        this.root.querySelector(
          '[data-ap-us-po-badge-item]'
        );

      this.inventoryUI =
        this.root.querySelector(
          '[data-ap-us-po-inventory]'
        );

      this.card =
        this.root.querySelector(
          '[data-ap-us-po-card]'
        );

      this.oos =
        this.root.querySelector(
          '[data-ap-us-po-oos]'
        );

      this.generalShippingRow =
        this.root.querySelector(
          '[data-ap-us-po-general-shipping]'
        );

      this.generalLabel =
        this.root.querySelector(
          '[data-ap-us-po-general-label]'
        );

      this.generalDate =
        this.root.querySelector(
          '[data-ap-us-po-date]'
        );

      this.firstItemShippingRow =
        this.root.querySelector(
          '[data-ap-us-po-first-item-shipping]'
        );

      this.firstItemLabel =
        this.root.querySelector(
          '[data-ap-us-po-first-item-label]'
        );

      this.firstItemDate =
        this.root.querySelector(
          '[data-ap-us-po-first-item-date]'
        );

      this.secondItemShippingRow =
        this.root.querySelector(
          '[data-ap-us-po-second-item-shipping]'
        );

      this.secondItemLabel =
        this.root.querySelector(
          '[data-ap-us-po-second-item-label]'
        );

      this.secondItemDate =
        this.root.querySelector(
          '[data-ap-us-po-second-item-date]'
        );
    }

    place() {
      if (
        this.gallery &&
        this.badge
      ) {
        const style =
          getComputedStyle(
            this.gallery
          );

        if (
          style.position ===
          'static'
        ) {
          this.gallery.style.position =
            'relative';
        }

        this.gallery.appendChild(
          this.badge
        );
      }

      if (
        this.inventoryBlock &&
        this.inventoryUI
      ) {
        this.inventoryBlock
          .insertAdjacentElement(
            'afterend',
            this.inventoryUI
          );
      } else if (
        this.info &&
        this.inventoryUI
      ) {
        this.info.appendChild(
          this.inventoryUI
        );
      }

      if (
        this.buyBlock &&
        this.card
      ) {
        this.buyBlock
          .insertAdjacentElement(
            'afterend',
            this.card
          );
      } else if (
        this.info &&
        this.card
      ) {
        this.info.appendChild(
          this.card
        );
      }

      if (
        this.card &&
        this.oos
      ) {
        this.card
          .insertAdjacentElement(
            'afterend',
            this.oos
          );
      }
    }

    currentVariantId() {
      const visibleInventory =
        this.variantInventory
          ?.querySelector(
            '[data-variant-id]:not([hidden])'
          );

      if (
        visibleInventory
          ?.dataset
          .variantId
      ) {
        return String(
          visibleInventory
            .dataset
            .variantId
        );
      }

      if (
        this.idInput?.value
      ) {
        return String(
          this.idInput.value
        );
      }

      const urlVariant =
        new URL(
          window.location.href
        )
          .searchParams
          .get('variant');

      if (urlVariant) {
        return String(
          urlVariant
        );
      }

      return String(
        this.variants[0]?.id ||
        ''
      );
    }

    currentVariant() {
      return (
        this.map.get(
          this.currentVariantId()
        ) ||
        this.variants[0] ||
        null
      );
    }

    isActive(variant) {
      if (
        !variant
          ?.preorderEnabled
      ) {
        return false;
      }

      if (
        !Number(
          variant.expiresEpoch
        )
      ) {
        return true;
      }

      return (
        Math.floor(
          Date.now() / 1000
        ) <
        Number(
          variant.expiresEpoch
        )
      );
    }

    mode(variant) {
      if (
        this.isActive(variant)
      ) {
        return 'preorder';
      }

      const outOfStock =
        this.root.dataset
          .autoOosContact ===
          'true' &&
        variant
          ?.inventoryTracked ===
          true &&
        Number(
          variant.inventoryQuantity
        ) <= 0;

      return outOfStock
        ? 'oos'
        : 'normal';
    }

    clearProperties() {
      this.form
        ?.querySelectorAll(
          '[data-ap-us-po-property]'
        )
        .forEach(
          (element) =>
            element.remove()
        );
    }

    addProperty(
      name,
      value
    ) {
      if (
        !this.form ||
        value === '' ||
        value == null
      ) {
        return;
      }

      const input =
        document.createElement(
          'input'
        );

      input.type =
        'hidden';

      input.name =
        `properties[${name}]`;

      input.value =
        String(value);

      input.dataset
        .apUsPoProperty =
        'true';

      this.form.appendChild(
        input
      );
    }

    hideShippingRows() {
      if (
        this.generalShippingRow
      ) {
        this.generalShippingRow.hidden =
          true;
      }

      if (
        this.firstItemShippingRow
      ) {
        this.firstItemShippingRow.hidden =
          true;
      }

      if (
        this.secondItemShippingRow
      ) {
        this.secondItemShippingRow.hidden =
          true;
      }

      if (
        this.generalLabel
      ) {
        this.generalLabel.textContent =
          'Estimated Ship Date';
      }

      if (
        this.generalDate
      ) {
        this.generalDate.textContent =
          '';
      }

      if (
        this.firstItemLabel
      ) {
        this.firstItemLabel.textContent =
          'Estimated Ship Date';
      }

      if (
        this.firstItemDate
      ) {
        this.firstItemDate.textContent =
          '';
      }

      if (
        this.secondItemLabel
      ) {
        this.secondItemLabel.textContent =
          'Estimated Ship Date';
      }

      if (
        this.secondItemDate
      ) {
        this.secondItemDate.textContent =
          '';
      }
    }

    reset() {
      this.section
        .classList
        .remove(
          'ap-us-po-special'
        );

      if (this.badge) {
        this.badge.hidden =
          true;
      }

      if (this.badgeItem) {
        this.badgeItem.textContent =
          '';

        this.badgeItem.hidden =
          true;
      }

      if (this.card) {
        this.card.hidden =
          true;
      }

      if (this.oos) {
        this.oos.hidden =
          true;
      }

      if (this.inventoryUI) {
        this.inventoryUI.hidden =
          true;
      }

      if (this.inventoryBlock) {
        this.inventoryBlock.hidden =
          false;
      }

      this.hideShippingRows();
      this.clearProperties();
    }

    applyNormal() {
      this.reset();
    }

    applyPreorder(variant) {
      this.reset();

      const warehouse =
        variant.warehouse
          ?.trim() ||
        this.root.dataset
          .defaultWarehouse ||
        '';

      const details =
        variant.details
          ?.trim() ||
        this.root.dataset
          .defaultDetails ||
        '';

      const shippingData =
        getVariantShippingData(
          variant,
          this.root.dataset
            .noDateText
        );

      const firstAutomaticName =
        shippingData
          .showFirstItem
          ? shippingData
              .firstItemLabel
          : shippingData
              .generalLabel;

      const secondAutomaticName =
        shippingData
          .showSecondItem
          ? shippingData
              .secondItemLabel
          : '';

      /*
       * 单独填写Variant简短说明时优先使用；
       * 留空时自动根据预售对象生成。
       */
      const shortNote =
        variant.shortNote
          ?.trim() ||
        buildAutomaticShortNote({
          firstItemName:
            firstAutomaticName,
          secondItemName:
            secondAutomaticName,
          productTitle:
            this.root.dataset
              .productTitle,
          variantTitle:
            variant.title,
          fallback:
            this.root.dataset
              .defaultShortNote
        });

      const badgeText =
        formatBadgeRange(
          shippingData
            .badgeStart,
          shippingData
            .badgeEnd
        );

      let badgeItemText = '';

      if (
        shippingData
          .showSecondItem
      ) {
        badgeItemText = [
          shippingData
            .firstItemLabel,
          shippingData
            .secondItemLabel
        ]
          .filter(Boolean)
          .join(' + ');
      } else if (
        shippingData
          .showFirstItem
      ) {
        badgeItemText =
          shippingData
            .firstItemLabel ||
          '';
      } else if (
        shippingData
          .showGeneral
      ) {
        badgeItemText =
          shippingData
            .generalLabel ||
          '';
      }

      if (
        this.root.dataset
          .showBadge ===
          'true' &&
        this.badge
      ) {
        this.badge.hidden =
          false;

        const badgeTitle =
          this.badge
            .querySelector(
              '[data-ap-us-po-badge-title]'
            );

        if (badgeTitle) {
          badgeTitle.textContent =
            this.root.dataset
              .preorderTitle ||
            'PRE-ORDER';
        }

        if (this.badgeItem) {
          this.badgeItem.textContent =
            badgeItemText;

          this.badgeItem.hidden =
            !badgeItemText;
        }

        const badgeDate =
          this.badge
            .querySelector(
              '[data-ap-us-po-badge-date]'
            );

        if (badgeDate) {
          badgeDate.textContent =
            badgeText;

          badgeDate.hidden =
            !badgeText;
        }
      }

      if (
        this.root.dataset
          .showCard ===
          'true' &&
        this.card
      ) {
        this.card.hidden =
          false;

        const cardTitle =
          this.card.querySelector(
            '[data-ap-us-po-card-title]'
          );

        if (cardTitle) {
          cardTitle.textContent =
            this.root.dataset
              .preorderTitle ||
            'PRE-ORDER';
        }

        if (
          shippingData
            .showGeneral &&
          this.generalShippingRow
        ) {
          this.generalShippingRow.hidden =
            false;

          if (
            this.generalLabel
          ) {
            this.generalLabel.textContent =
              buildShipDateLabel(
                shippingData
                  .generalLabel
              );
          }

          if (
            this.generalDate
          ) {
            this.generalDate.textContent =
              shippingData
                .general
                .text;
          }
        }

        if (
          shippingData
            .showFirstItem &&
          this.firstItemShippingRow
        ) {
          this.firstItemShippingRow.hidden =
            false;

          if (
            this.firstItemLabel
          ) {
            this.firstItemLabel.textContent =
              buildShipDateLabel(
                shippingData
                  .firstItemLabel
              );
          }

          if (
            this.firstItemDate
          ) {
            this.firstItemDate.textContent =
              shippingData
                .firstItem
                .text;
          }
        }

        if (
          shippingData
            .showSecondItem &&
          this.secondItemShippingRow
        ) {
          this.secondItemShippingRow.hidden =
            false;

          if (
            this.secondItemLabel
          ) {
            this.secondItemLabel.textContent =
              buildShipDateLabel(
                shippingData
                  .secondItemLabel
              );
          }

          if (
            this.secondItemDate
          ) {
            this.secondItemDate.textContent =
              shippingData
                .secondItem
                .text;
          }
        }

        const warehouseElement =
          this.card.querySelector(
            '[data-ap-us-po-warehouse]'
          );

        if (
          warehouseElement
        ) {
          warehouseElement.textContent =
            warehouse;
        }

        const shortNoteElement =
          this.card.querySelector(
            '[data-ap-us-po-short-note]'
          );

        if (
          shortNoteElement
        ) {
          shortNoteElement.textContent =
            shortNote;
        }

        const detailsTitle =
          this.card.querySelector(
            '[data-ap-us-po-details-title]'
          );

        if (
          detailsTitle
        ) {
          detailsTitle.textContent =
            this.root.dataset
              .shippingDetailsTitle ||
            'Shipping Details';
        }

        const detailsElement =
          this.card.querySelector(
            '[data-ap-us-po-details]'
          );

        if (
          detailsElement
        ) {
          detailsElement.textContent =
            details;
        }

        const splitNote =
          this.card.querySelector(
            '[data-ap-us-po-split-note]'
          );

        if (
          splitNote
        ) {
          splitNote.hidden =
            !variant
              .splitShipping;

          splitNote.textContent =
            variant
              .splitShipping
              ? (
                  this.root.dataset
                    .splitNote ||
                  ''
                )
              : '';
        }
      }

      if (
        this.inventoryBlock
      ) {
        this.inventoryBlock.hidden =
          true;
      }

      if (
        this.inventoryUI
      ) {
        this.inventoryUI.hidden =
          false;

        this.inventoryUI
          .classList
          .remove(
            'is-oos'
          );

        this.inventoryUI.textContent =
          'Pre-order available';
      }

      if (
        this.atcButton
      ) {
        this.atcButton.disabled =
          false;
      }

      if (
        this.atcText
      ) {
        this.atcText.textContent =
          this.root.dataset
            .preorderButtonText ||
          'PRE-ORDER NOW';
      }

      if (
        this.root.dataset
          .hideDynamicCheckout ===
        'true'
      ) {
        this.section
          .classList
          .add(
            'ap-us-po-special'
          );
      }

      this.addProperty(
        'Pre-order',
        'Yes'
      );

      const itemNames = [
        shippingData
          .firstItemLabel,
        shippingData
          .secondItemLabel,
        shippingData
          .generalLabel
      ]
        .filter(Boolean)
        .join(' + ');

      if (itemNames) {
        this.addProperty(
          'Pre-order item',
          itemNames
        );
      }

      if (
        shippingData
          .showGeneral
      ) {
        this.addProperty(
          buildShipDateLabel(
            shippingData
              .generalLabel
          ),
          shippingData
            .general
            .text
        );
      }

      if (
        shippingData
          .showFirstItem
      ) {
        this.addProperty(
          buildShipDateLabel(
            shippingData
              .firstItemLabel
          ),
          shippingData
            .firstItem
            .text
        );
      }

      if (
        shippingData
          .showSecondItem
      ) {
        this.addProperty(
          buildShipDateLabel(
            shippingData
              .secondItemLabel
          ),
          shippingData
            .secondItem
            .text
        );
      }

      this.addProperty(
        'Shipping Warehouse',
        warehouse
      );

      if (
        variant
          .splitShipping
      ) {
        this.addProperty(
          'Separate shipments may apply',
          'Yes'
        );
      }
    }

    applyOOS() {
      this.reset();

      if (
        this.oos
      ) {
        this.oos.hidden =
          false;

        const title =
          this.oos.querySelector(
            '[data-ap-us-po-oos-title]'
          );

        if (title) {
          title.textContent =
            this.root.dataset
              .oosTitle ||
            'OUT OF STOCK';
        }

        const note =
          this.oos.querySelector(
            '[data-ap-us-po-oos-note]'
          );

        if (note) {
          note.textContent =
            this.root.dataset
              .oosNote ||
            'Shipping availability: Please contact customer service.';
        }

        const link =
          this.oos.querySelector(
            '[data-ap-us-po-contact]'
          );

        if (link) {
          link.textContent =
            this.root.dataset
              .contactText ||
            'CONTACT US';

          link.href =
            this.root.dataset
              .contactUrl ||
            '#';
        }
      }

      if (
        this.inventoryBlock
      ) {
        this.inventoryBlock.hidden =
          true;
      }

      if (
        this.inventoryUI
      ) {
        this.inventoryUI.hidden =
          false;

        this.inventoryUI
          .classList
          .add(
            'is-oos'
          );

        this.inventoryUI.textContent =
          this.root.dataset
            .oosTitle ||
          'OUT OF STOCK';
      }

      if (
        this.atcButton
      ) {
        this.atcButton.disabled =
          true;
      }

      if (
        this.atcText
      ) {
        this.atcText.textContent =
          this.root.dataset
            .oosTitle ||
          'OUT OF STOCK';
      }

      if (
        this.root.dataset
          .hideDynamicCheckout ===
        'true'
      ) {
        this.section
          .classList
          .add(
            'ap-us-po-special'
          );
      }

      this.clearProperties();
    }

    render() {
      const variant =
        this.currentVariant();

      if (!variant) return;

      const mode =
        this.mode(variant);

      if (
        mode ===
        'preorder'
      ) {
        this.applyPreorder(
          variant
        );
      } else if (
        mode ===
        'oos'
      ) {
        this.applyOOS();
      } else {
        this.applyNormal();
      }
    }

    renderSoon() {
      requestAnimationFrame(
        () => {
          setTimeout(
            () =>
              this.render(),
            120
          );
        }
      );
    }

    observe() {
      this.onChange =
        () =>
          this.renderSoon();

      this.section
        .addEventListener(
          'change',
          this.onChange
        );

      this.cleanup.push(
        () => {
          this.section
            .removeEventListener(
              'change',
              this.onChange
            );
        }
      );

      this.onClick =
        (event) => {
          if (
            event.target.closest(
              'variant-picker, [data-option-value], [data-option-selector]'
            )
          ) {
            this.renderSoon();
          }
        };

      this.section
        .addEventListener(
          'click',
          this.onClick
        );

      this.cleanup.push(
        () => {
          this.section
            .removeEventListener(
              'click',
              this.onClick
            );
        }
      );

      this.observer =
        new MutationObserver(
          () =>
            this.renderSoon()
        );

      if (
        this.variantInventory
      ) {
        this.observer.observe(
          this.variantInventory,
          {
            subtree: true,
            attributes: true,
            attributeFilter: [
              'hidden'
            ]
          }
        );
      }

      if (
        this.idInput
      ) {
        this.observer.observe(
          this.idInput,
          {
            attributes: true,
            attributeFilter: [
              'value',
              'disabled'
            ]
          }
        );
      }

      this.cleanup.push(
        () =>
          this.observer
            .disconnect()
      );
    }

    scheduleExpiry() {
      const nextExpiry =
        this.variants
          .map(
            (variant) =>
              Number(
                variant
                  .expiresEpoch
              ) ||
              0
          )
          .filter(
            (timestamp) =>
              timestamp >
              Math.floor(
                Date.now() /
                1000
              )
          )
          .sort(
            (a, b) =>
              a - b
          )[0];

      if (!nextExpiry) {
        return;
      }

      const delay =
        Math.min(
          Math.max(
            nextExpiry *
              1000 -
              Date.now() +
              250,
            250
          ),
          2147483647
        );

      this.timer =
        setTimeout(
          () =>
            this.render(),
          delay
        );
    }

    destroy() {
      this.cleanup.forEach(
        (callback) =>
          callback()
      );

      if (
        this.timer
      ) {
        clearTimeout(
          this.timer
        );
      }

      delete this.root[KEY];
    }
  }

  const init = (root) => {
    if (
      !root ||
      root[KEY]
    ) {
      return;
    }

    root[KEY] =
      new APUSImpactPreorder(
        root
      );
  };

  const initAll = (
    scope = document
  ) => {
    scope
      .querySelectorAll(
        SELECTOR
      )
      .forEach(init);
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () =>
        initAll()
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) =>
      initAll(
        event.target
      )
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      event.target
        .querySelectorAll(
          SELECTOR
        )
        .forEach(
          (root) => {
            root[KEY]
              ?.destroy();
          }
        );
    }
  );
})();